import { MyShiftAssignmentTable } from "@/components/crud/table/user/MyShiftAssignmentTable";
import { DashboardHeader } from "@/components/dashboard-header";
import { DateSelector } from "@/components/crud/filters/DateSelector";
import { SidebarInset } from "@/components/ui/sidebar";
import MyShiftAssignmentList from "@/components/user/my-assignments/MyAssignmentList";
import useMyShiftAssignments from "@/hooks/api/shift-assignment/useMyShiftAssignment";
import { createFileRoute } from "@tanstack/react-router";
import { dayjs } from "@/lib/date-utils";
import { useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";

export const Route = createFileRoute("/user/_layout/attendance/shifts")({
  component: RouteComponent,
});

function RouteComponent() {
  return <MyShifts />;
}

function MyShifts() {
  const [showFilters, setShowFilters] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const myShiftAssignmentsQuery = useMyShiftAssignments(
    range?.from && range?.to
      ? {
          start_date: dayjs(range.from).format("YYYY-MM-DD"),
          end_date: dayjs(range.to).format("YYYY-MM-DD"),
          page,
          page_size: pageSize,
        }
      : { page, page_size: pageSize },
  );
  const myShiftAssignments = myShiftAssignmentsQuery.data?.results ?? [];
  const count = myShiftAssignmentsQuery.data?.count ?? 0;
  const loading = myShiftAssignmentsQuery.isPending;

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize],
  );
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [range, setPage]);

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="w-full space-y-4 p-6 pt-0">
        <PageHeader
          title="Shift Assignments"
          description="View all your assigned shifts, their status, and submission details."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
        />

        <FiltersPanel show={showFilters} onClear={() => setRange(undefined)}>
          <DateSelector
            value={range}
            onApply={setRange}
            onClear={() => setRange(undefined)}
            label="Filter by date range"
          />
        </FiltersPanel>

        <div className="lg:block hidden">
          <MyShiftAssignmentTable
            myShiftAssignments={myShiftAssignments}
            loading={loading}
          />
        </div>
        <div className="block lg:hidden">
          <MyShiftAssignmentList
            myShiftAssignments={myShiftAssignments}
            loading={loading}
          />
        </div>

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={myShiftAssignmentsQuery.isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>
    </SidebarInset>
  );
}
